/**
 * Structural CSS only — every color/spacing/radius value is a `var(--cos-*)`
 * custom property set by `applyTheme.ts`, never a literal, so this string
 * never changes across brands. Kept as one template literal (not a CSS
 * file) so it ships inline in the constructed stylesheet — no second
 * request, no leakage either direction (PLAN.md "Isolation & size").
 */
export const BASE_STYLES = `
  * { box-sizing: border-box; }

  .cos-root {
    position: fixed;
    z-index: 2147483000;
    font-family: var(--cos-font-family);
    color: var(--cos-text-primary);
  }
  /* --cos-keyboard-inset is how much of the layout viewport an on-screen
     keyboard covers, measured from the visual viewport (useVisualViewport)
     and 0 whenever no keyboard is up. iOS does not resize the page for its
     keyboard, so a position: fixed launcher — and the panel anchored to it
     — otherwise sits underneath one. Unset (no VisualViewport API, or inline
     mode, where the host page owns the layout) it falls back to 0 and this
     is the geometry it always was. */
  .cos-root[data-position="bottom-right"] { right: 20px; bottom: calc(20px + var(--cos-keyboard-inset, 0px)); }
  .cos-root[data-position="bottom-left"] { left: 20px; bottom: calc(20px + var(--cos-keyboard-inset, 0px)); }

  .cos-launcher {
    width: 56px;
    height: 56px;
    border-radius: var(--cos-radius-launcher);
    background: var(--cos-brand);
    color: var(--cos-on-brand);
    border: none;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
  }
  .cos-launcher:hover { background: var(--cos-brand-hover); }
  .cos-launcher:active { background: var(--cos-brand-active); }
  .cos-launcher:focus-visible { outline: 2px solid var(--cos-focus-ring); outline-offset: 2px; }

  .cos-feedback { margin-top: 2px; }
  .cos-feedback-thumbs { display: flex; gap: 4px; }
  .cos-feedback-thumbs button {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 12px;
    opacity: 0.45;
    padding: 2px 4px;
  }
  .cos-feedback-thumbs button:hover { opacity: 1; }
  /* The tapped thumb stays lit. Without it the click does nothing visible
     and shoppers tap again, which reads as a change of mind. */
  .cos-feedback-thumbs button[data-active] { opacity: 1; }

  .cos-feedback-comment { display: flex; gap: 4px; align-items: center; margin-top: 4px; }
  .cos-feedback-comment input {
    flex: 1;
    min-width: 0;
    border: 1px solid var(--cos-border);
    border-radius: 6px;
    padding: 4px 8px;
    font: inherit;
    font-size: 12px;
    color: var(--cos-text-primary);
    background: var(--cos-surface);
  }
  .cos-feedback-comment button[type="submit"] {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 12px;
    padding: 2px 4px;
    color: var(--cos-brand);
  }
  .cos-feedback-comment button[type="submit"]:disabled { opacity: 0.4; cursor: default; }
  .cos-feedback-dismiss {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 14px;
    line-height: 1;
    padding: 2px 4px;
    opacity: 0.45;
  }
  .cos-feedback-dismiss:hover { opacity: 1; }
  .cos-feedback-thanks { display: inline-block; margin-top: 4px; font-size: 12px; opacity: 0.55; }

  .cos-order-confirm-button { margin-top: 8px; }

  .cos-launcher-wrap { display: flex; align-items: flex-end; gap: 8px; flex-direction: row-reverse; }

  .cos-unread-badge {
    position: absolute;
    top: -2px;
    right: -2px;
    min-width: 22px;
    height: 22px;
    padding: 0 5px;
    box-sizing: border-box;
    border-radius: 11px;
    background: #d93025;
    border: 2px solid #fff;
    color: #fff;
    font-size: 11px;
    line-height: 18px;
    font-weight: 700;
    text-align: center;
    animation: cos-badge-ping 1.8s ease-out infinite;
  }

  @keyframes cos-badge-ping {
    0% { box-shadow: 0 0 0 0 rgba(217, 48, 37, 0.55); }
    70%, 100% { box-shadow: 0 0 0 9px rgba(217, 48, 37, 0); }
  }

  .cos-launcher { position: relative; }

  /* The teaser is what makes an agent's reply land when the shopper has
     closed the widget and wandered off. */
  .cos-teaser {
    max-width: 240px;
    text-align: left;
    background: var(--cos-surface);
    color: var(--cos-text-primary);
    border: 1px solid var(--cos-border);
    border-left: 4px solid #d93025;
    border-radius: var(--cos-radius);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.24);
    padding: 10px 12px;
    font-size: 14px;
    line-height: 1.35;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 2px;
    animation: cos-teaser-in 0.25s ease-out;
  }
  .cos-teaser-label { font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: #d93025; }
  .cos-teaser-text {
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }

  @keyframes cos-teaser-in {
    from { opacity: 0; transform: translateY(6px); }
  }

  .cos-launcher[data-state="unread"],
  .cos-launcher[data-state="typing"] { animation: cos-attention 1.6s ease-in-out 3; }

  @keyframes cos-attention {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.08); }
  }

  /* Attention is a nudge, never a demand — motion is off for anyone who
     asked for it to be. */
  @media (prefers-reduced-motion: reduce) {
    .cos-launcher[data-state="unread"],
    .cos-launcher[data-state="typing"] { animation: none; }
    .cos-unread-badge, .cos-teaser { animation: none; }
  }

  .cos-panel {
    position: absolute;
    bottom: 72px;
    right: 0;
    width: 360px;
    max-width: calc(100vw - 40px);
    /* The *visible* height, not the layout one: with a keyboard up, 100vh
       still counts the rows of keys, and a panel sized to it puts its own
       composer behind them. 120px is the launcher plus the gaps above and
       below it, so the panel always stops short of the visual viewport's
       top edge. */
    height: min(600px, calc(var(--cos-viewport-height, 100vh) - 120px));
    background: var(--cos-background);
    border: 1px solid var(--cos-border);
    border-radius: var(--cos-radius);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.24);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .cos-root[data-position="bottom-left"] .cos-panel { right: auto; left: 0; }
  .cos-root[data-mode="fullscreen"] .cos-panel {
    position: fixed;
    /* Anchored to the visual viewport rather than inset: 0, so an open
       keyboard shortens the panel from the bottom instead of sliding its
       composer out of sight. offset-top matters on iOS, where the visual
       viewport also *moves* when the page scrolls under a focused field. */
    top: var(--cos-viewport-offset-top, 0px);
    left: 0;
    right: 0;
    bottom: auto;
    width: 100vw;
    max-width: 100vw;
    height: var(--cos-viewport-height, 100dvh);
    border-radius: 0;
    border: none;
  }
  .cos-root[data-mode="inline"] .cos-panel {
    position: static;
    width: 100%;
    height: 100%;
  }

  .cos-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: calc(12px * var(--cos-spacing-scale)) calc(16px * var(--cos-spacing-scale));
    border-bottom: 1px solid var(--cos-border-subtle);
    background: var(--cos-surface);
  }
  .cos-bot-name { font-weight: 600; flex: 1; }
  .cos-close {
    background: none;
    border: none;
    color: var(--cos-text-secondary);
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    padding: 4px;
  }

  .cos-messages {
    flex: 1;
    overflow-y: auto;
    padding: calc(12px * var(--cos-spacing-scale));
    display: flex;
    flex-direction: column;
    gap: calc(10px * var(--cos-spacing-scale));
  }

  .cos-message { display: flex; flex-direction: column; gap: 4px; max-width: 85%; }
  .cos-message[data-role="user"] { align-self: flex-end; align-items: flex-end; }
  .cos-message[data-role="assistant"],
  .cos-message[data-role="agent"] { align-self: flex-start; }
  .cos-message[data-role="system"] { align-self: center; max-width: 100%; }

  .cos-bubble {
    padding: calc(8px * var(--cos-spacing-scale)) calc(12px * var(--cos-spacing-scale));
    border-radius: var(--cos-radius);
    background: var(--cos-surface);
    border: 1px solid var(--cos-border-subtle);
  }
  .cos-message[data-role="user"] .cos-bubble { background: var(--cos-brand); color: var(--cos-on-brand); border-color: transparent; }
  .cos-bubble p { margin: 0 0 6px; }
  .cos-bubble p:last-child { margin-bottom: 0; }
  .cos-bubble ul { margin: 0 0 6px; padding-left: 18px; }
  .cos-bubble code { background: rgba(127, 127, 127, 0.15); padding: 1px 4px; border-radius: 4px; }

  .cos-notice { padding: 8px 12px; border-radius: var(--cos-radius); font-size: 13px; }
  .cos-notice-info { background: var(--cos-brand-subtle); }
  .cos-notice-warning { background: color-mix(in srgb, var(--cos-danger) 20%, var(--cos-surface)); }
  .cos-notice-error { background: var(--cos-danger); color: var(--cos-on-danger); }

  .cos-links, .cos-quick-replies, .cos-actions { display: flex; flex-wrap: wrap; gap: 6px; }
  .cos-chip, .cos-chip-button {
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    border-radius: 999px;
    border: 1px solid var(--cos-border);
    background: var(--cos-surface-raised);
    color: var(--cos-text-primary);
    font-size: 13px;
    text-decoration: none;
    cursor: pointer;
  }
  .cos-chip-button:hover { border-color: var(--cos-brand); }
  .cos-chip-button:focus-visible { outline: 2px solid var(--cos-focus-ring); outline-offset: 1px; }
  .cos-chip-button:disabled, .cos-chip-button[disabled] { opacity: 0.5; cursor: default; }
  .cos-chip-button:disabled:hover { border-color: var(--cos-border); }

  .cos-followup { display: flex; flex-direction: column; gap: 8px; }
  .cos-followup-question { font-size: 13px; font-weight: 600; color: var(--cos-text-primary); }
  .cos-followup-options { display: flex; flex-direction: column; gap: 6px; }
  .cos-followup-options-inline .cos-followup-options { flex-direction: row; flex-wrap: wrap; }
  .cos-followup-option {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    width: 100%;
    min-height: 44px;
    padding: 8px 12px;
    border-radius: var(--cos-radius);
    border: 1px solid var(--cos-border);
    background: var(--cos-surface-raised);
    color: var(--cos-text-primary);
    font-size: 13px;
    text-align: left;
    cursor: pointer;
  }
  .cos-followup-option:hover { border-color: var(--cos-brand); }
  .cos-followup-option:focus-visible { outline: 2px solid var(--cos-focus-ring); outline-offset: 1px; }
  .cos-followup-option:disabled, .cos-followup-option[disabled] { opacity: 0.5; cursor: default; }
  .cos-followup-option:disabled:hover { border-color: var(--cos-border); }
  .cos-followup-option-label { font-weight: 500; }
  .cos-followup-option-description { font-size: 12px; color: var(--cos-text-secondary); }
  .cos-followup-hint { font-size: 12px; color: var(--cos-text-secondary); }

  .cos-products { display: flex; gap: 10px; }
  .cos-products-list { flex-direction: column; }
  .cos-products-carousel { overflow-x: auto; padding-bottom: 4px; }
  .cos-product-card {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex: 0 0 auto;
    width: 160px;
    border: 1px solid var(--cos-border);
    border-radius: var(--cos-radius);
    padding: 8px;
    text-decoration: none;
    color: var(--cos-text-primary);
    background: var(--cos-surface-raised);
  }
  .cos-products-list .cos-product-card { width: 100%; }
  .cos-product-image { width: 64px; height: 64px; flex: 0 0 auto; aspect-ratio: 1; object-fit: cover; border-radius: calc(var(--cos-radius) - 4px); }
  .cos-product-card-body { min-width: 0; flex: 1 1 auto; }
  .cos-product-title { font-size: 13px; font-weight: 600; }
  .cos-product-price { font-size: 13px; color: var(--cos-text-secondary); }
  .cos-product-stock { font-size: 12px; color: var(--cos-danger); }
  .cos-product-note { font-size: 12px; color: var(--cos-text-muted); margin-top: 4px; }

  .cos-order-confirm { border: 1px solid var(--cos-border); border-radius: var(--cos-radius); padding: 10px; }
  .cos-order-title { font-weight: 600; margin-bottom: 6px; }
  .cos-order-items { margin: 0 0 8px; padding-left: 18px; font-size: 13px; }
  .cos-order-total { font-weight: 600; }

  .cos-form { display: flex; flex-direction: column; gap: 8px; }
  .cos-form-field { display: flex; flex-direction: column; gap: 4px; font-size: 13px; }
  .cos-form-field input, .cos-form-field textarea {
    border: 1px solid var(--cos-border);
    border-radius: 8px;
    padding: 6px 8px;
    font: inherit;
    background: var(--cos-surface-raised);
    color: var(--cos-text-primary);
  }

  .cos-typing { display: flex; gap: 3px; padding: 4px 0; }
  .cos-typing span { width: 6px; height: 6px; border-radius: 50%; background: var(--cos-text-muted); animation: cos-bounce 1.2s infinite ease-in-out; }
  .cos-typing span:nth-child(2) { animation-delay: 0.15s; }
  .cos-typing span:nth-child(3) { animation-delay: 0.3s; }
  @keyframes cos-bounce { 0%, 80%, 100% { opacity: 0.3; } 40% { opacity: 1; } }
  @media (prefers-reduced-motion: reduce) {
    .cos-typing span { animation: none; opacity: 0.6; }
  }

  /* The "notify me" card: ours first, the browser's dialog only after a yes. */
  .cos-push {
    margin: 0 calc(10px * var(--cos-spacing-scale)) 8px;
    padding: 10px 12px;
    border: 1px solid var(--cos-border);
    border-radius: var(--cos-radius);
    background: var(--cos-surface);
    color: var(--cos-text-primary);
    font-size: 13px;
    line-height: 1.35;
  }
  .cos-push p { margin: 0 0 8px; }
  .cos-push div { display: flex; gap: 8px; }
  .cos-push button {
    font: inherit;
    padding: 6px 12px;
    border-radius: var(--cos-radius);
    border: 1px solid var(--cos-border);
    background: transparent;
    color: inherit;
    cursor: pointer;
  }
  .cos-push .cos-push-yes { background: var(--cos-brand); color: var(--cos-on-brand); border-color: transparent; }
  .cos-push button:disabled { opacity: 0.6; cursor: default; }
  .cos-push button:focus-visible { outline: 2px solid var(--cos-focus-ring); outline-offset: 2px; }
  .cos-composer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: calc(10px * var(--cos-spacing-scale));
    border-top: 1px solid var(--cos-border-subtle);
    background: var(--cos-surface);
  }
  /* flex-end, not center: the field grows upward as the shopper writes, and
     the controls beside it should stay on the bottom line with the caret
     rather than drift to the middle of a five-line box. */
  .cos-composer-row { display: flex; gap: 8px; align-items: flex-end; min-width: 0; }
  .cos-file-input { display: none; }
  .cos-icon-button {
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 1px solid var(--cos-border);
    border-radius: 999px;
    background: var(--cos-background);
    color: var(--cos-text-primary);
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
  }
  .cos-icon-button:disabled { opacity: 0.4; cursor: default; }
  .cos-menu { position: relative; display: flex; }
  /* Opens upward: the composer sits at the bottom of the panel, and a
     menu dropping down would fall outside it. */
  .cos-menu-popover {
    position: absolute;
    bottom: calc(100% + 6px);
    left: 0;
    z-index: 2;
    min-width: 200px;
    padding: 4px;
    border: 1px solid var(--cos-border);
    border-radius: 10px;
    background: var(--cos-surface);
    box-shadow: 0 8px 24px rgb(0 0 0 / 0.18);
  }
  .cos-menu-popover button {
    display: block;
    width: 100%;
    padding: 8px 10px;
    border: none;
    border-radius: 6px;
    background: none;
    color: var(--cos-text-primary);
    font: inherit;
    text-align: left;
    cursor: pointer;
  }
  .cos-menu-popover button:hover { background: var(--cos-background); }
  .cos-attachments { display: flex; gap: 6px; flex-wrap: wrap; }
  .cos-attachment { position: relative; }
  .cos-attachment img {
    width: 56px;
    height: 56px;
    object-fit: cover;
    border-radius: 8px;
    display: block;
  }
  .cos-attachment button {
    position: absolute;
    top: -6px;
    right: -6px;
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    border-radius: 999px;
    background: var(--cos-text-primary);
    color: var(--cos-surface);
    font-size: 13px;
    line-height: 1;
    cursor: pointer;
  }
  .cos-message-images { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 6px; }
  .cos-image-button { padding: 0; border: none; background: none; cursor: zoom-in; }
  .cos-message-images img {
    max-width: 180px;
    max-height: 180px;
    border-radius: 10px;
    display: block;
  }
  .cos-lightbox {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgb(0 0 0 / 0.82);
    cursor: zoom-out;
  }
  .cos-lightbox img { max-width: 100%; max-height: 100%; border-radius: 8px; }
  .cos-composer textarea {
    /* min-width: 0 is the whole fix for a Send button cut off on a phone.
       A text field's default intrinsic minimum is about twenty characters
       wide, and min-width: auto on a flex item means it refuses to shrink
       past that — so on a narrow panel the row grows wider than the panel
       and overflow: hidden takes the difference out of whatever is last in
       the row. */
    flex: 1 1 auto;
    min-width: 0;
    border: 1px solid var(--cos-border);
    /* Not 999px: at one line that reads as the same pill it always was, but
       a grown field would round its corners by half its height. */
    border-radius: 18px;
    padding: 8px 14px;
    font: inherit;
    /* The height is written inline by the composer, which measures against
       this line-height — left at "normal" it would be sizing the field from
       a guess. Unitless so it still tracks a host page's own font size, and
       so 1.35em below is the same length the measurement uses.
       resize: none because a drag handle fights the auto-sizing, and the
       cap is a deliberate share of a panel only 600px tall. */
    line-height: 1.35;
    resize: none;
    /* 18px is the padding and borders around the text. The min is one line,
       so the composer opens no taller than it ever was; the max is the same
       five lines MAX_ROWS enforces — belt and braces for the first paint,
       before the inline height is measured. */
    min-height: calc(1.35em + 18px);
    max-height: calc(6.75em + 18px);
    overflow-y: hidden;
    background: var(--cos-background);
    color: var(--cos-text-primary);
  }
  .cos-composer textarea:focus-visible { outline: 2px solid var(--cos-focus-ring); }
  .cos-composer button[type="submit"] {
    /* Never the item that gives way when the row is tight. */
    flex: 0 0 auto;
    white-space: nowrap;
    border: none;
    border-radius: 999px;
    background: var(--cos-brand);
    color: var(--cos-on-brand);
    padding: 8px 16px;
    font-weight: 600;
    cursor: pointer;
  }
  .cos-composer button[type="submit"]:disabled { opacity: 0.5; cursor: default; }

  /* iOS Safari zooms the whole page in when a field it focuses renders text
     under 16px, and the shopper is then left at that zoom level — the widget
     is embedded, so it cannot reach the host page's viewport meta to opt
     out. Sizing the fields themselves is the one fix that is ours to make.
     max() rather than a flat 16px so a host page with larger body text
     keeps its own scale. Coarse pointers only: this is a touch-keyboard
     behaviour, and desktop fields stay at the host's own size. */
  @media (pointer: coarse) {
    .cos-composer textarea,
    .cos-feedback-comment input,
    .cos-form-field input,
    .cos-form-field textarea { font-size: max(16px, 1em); }
  }
`;
